import React, { useState, useEffect, useMemo } from 'react';
import { arApi } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { Loader2, MessageSquare, Plus, X, AlertTriangle, IndianRupee, FileText } from 'lucide-react';

const _fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/**
 * Consolidated statement reminder dialog.
 * Renders all pending invoices for a customer, the auto-built message preview,
 * editable recipients and message, then POSTs to /api/ar/customers/{id}/bulk-reminder.
 */
const BulkReminderDialog = ({ open, customer, onClose, onSent }) => {
  const customerId = customer?.id;
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [phones, setPhones] = useState([]); // selected, normalized as entered
  const [phoneInput, setPhoneInput] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !customerId) return;
    setLoading(true);
    setPreview(null);
    setMessage('');
    arApi.bulkReminderPreview(customerId)
      .then(r => {
        setPreview(r.data);
        setPhones(r.data.suggested_phones || []);
        setMessage(r.data.message_preview || '');
      })
      .catch(e => {
        toast.error(e?.response?.data?.detail || 'Failed to load statement preview');
        onClose?.();
      })
      .finally(() => setLoading(false));
  }, [open, customerId, onClose]);

  const addPhone = () => {
    const v = phoneInput.trim();
    if (!v) return;
    if (phones.includes(v)) {
      setPhoneInput('');
      return;
    }
    setPhones([...phones, v]);
    setPhoneInput('');
  };
  const removePhone = (p) => setPhones(phones.filter(x => x !== p));

  const send = async () => {
    if (phones.length === 0) {
      toast.error('Add at least one WhatsApp recipient');
      return;
    }
    if (!preview || preview.invoice_count === 0) {
      toast.error('No open invoices to remind');
      return;
    }
    setSending(true);
    try {
      const res = await arApi.sendBulkReminder(customerId, {
        phones,
        message: message.trim() || undefined,
      });
      const sent = res.data?.sent_to?.length || 0;
      const failed = res.data?.failed?.length || 0;
      toast.success(`Statement reminder sent to ${sent} recipient(s)${failed ? ` · ${failed} failed` : ''}`);
      onSent?.(res.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send statement reminder');
    } finally {
      setSending(false);
    }
  };

  const totals = useMemo(() => ({
    outstanding: preview?.total_outstanding || 0,
    overdue: preview?.total_overdue || 0,
    count: preview?.invoice_count || 0,
  }), [preview]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !sending && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bulk-reminder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-violet-600 dark:text-violet-400" />
            Send Statement Reminder
          </DialogTitle>
          <DialogDescription>
            Sends one WhatsApp message listing every pending invoice with a grand total.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-2 py-2">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : !preview ? null : (
          <div className="space-y-4">
            {/* Customer & totals */}
            <div className="grid grid-cols-3 gap-2 rounded-lg border bg-muted/40 p-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Customer</p>
                <p className="text-sm font-semibold truncate" data-testid="brd-customer-name">{preview.customer_name}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Open invoices</p>
                <p className="text-sm font-semibold tabular-nums" data-testid="brd-invoice-count">{totals.count}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
                  <IndianRupee className="h-3 w-3" /> Outstanding
                </p>
                <p className={`text-sm font-bold tabular-nums ${totals.outstanding > 0 ? 'text-rose-600 dark:text-rose-400' : ''}`}
                   data-testid="brd-total-outstanding">{_fmtINR(totals.outstanding)}</p>
                {totals.overdue > 0 && (
                  <p className="text-[11px] text-rose-600 dark:text-rose-400 font-medium" data-testid="brd-total-overdue">
                    Overdue: {_fmtINR(totals.overdue)}
                  </p>
                )}
              </div>
            </div>

            {totals.count === 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-800 p-3 text-sm text-amber-700 dark:text-amber-300 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /> No open invoices found — nothing to remind.
              </div>
            )}

            {/* Invoice list preview */}
            {preview.invoices?.length > 0 && (
              <div>
                <Label className="text-xs">Invoices included ({preview.invoices.length})</Label>
                <div className="mt-1 max-h-44 overflow-y-auto rounded-lg border divide-y" data-testid="brd-invoice-list">
                  {preview.invoices.map(inv => (
                    <div key={`${inv.invoice_number}-${inv.invoice_date}`} className="px-3 py-2 flex items-center justify-between gap-2 text-sm">
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{inv.invoice_number || inv.co_number || '—'}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {inv.invoice_date || '—'}
                          {inv.due_date && ` · due ${inv.due_date}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {inv.days_overdue > 0 && (
                          <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/20 dark:text-rose-300 text-[10px]">
                            {inv.days_overdue}d overdue
                          </Badge>
                        )}
                        <p className="tabular-nums font-semibold">{_fmtINR(inv.balance)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recipients */}
            <div>
              <Label className="text-xs">WhatsApp recipients</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="brd-recipients">
                {phones.map(p => (
                  <Badge key={p} variant="secondary" className="gap-1 pl-2 pr-1 py-1 text-xs">
                    {p}
                    <button
                      type="button"
                      onClick={() => removePhone(p)}
                      className="ml-0.5 rounded hover:bg-muted-foreground/20 p-0.5"
                      data-testid={`brd-remove-phone-${p}`}
                      aria-label="Remove phone"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {phones.length === 0 && (
                  <span className="text-xs text-muted-foreground">No recipients selected.</span>
                )}
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="Add phone (with country code)"
                  value={phoneInput}
                  onChange={e => setPhoneInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPhone(); } }}
                  data-testid="brd-phone-input"
                />
                <Button type="button" variant="outline" onClick={addPhone} data-testid="brd-add-phone">
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
            </div>

            {/* Message */}
            <div>
              <Label className="text-xs">WhatsApp message (editable)</Label>
              <Textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={3}
                className="text-xs"
                data-testid="brd-message"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Single body variable <code>{'{{1}}'}</code> of your DoubleTick template. Full invoice details go in the attached PDF.
              </p>
            </div>

            {/* PDF preview link */}
            <div className="rounded-lg border bg-violet-50 dark:bg-violet-500/10 border-violet-200 dark:border-violet-800 p-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-5 w-5 text-violet-600 dark:text-violet-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">Statement PDF attachment</p>
                  <p className="text-[11px] text-muted-foreground">
                    Customer receives this PDF with all invoice details.
                  </p>
                </div>
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/ar/customers/${customerId}/statement.pdf`}
                target="_blank"
                rel="noreferrer"
                data-testid="brd-preview-pdf"
                onClick={async (e) => {
                  // Browsers don't send Authorization headers on <a target=_blank>.
                  // Fetch with token, then open Blob URL.
                  e.preventDefault();
                  try {
                    const res = await arApi.bulkReminderPreview(customerId); // warm token
                    void res;
                    const url = `${process.env.REACT_APP_BACKEND_URL}/api/ar/customers/${customerId}/statement.pdf`;
                    const fetchRes = await fetch(url, {
                      headers: { 'Authorization': `Bearer ${localStorage.getItem('siddhi_token') || ''}` },
                    });
                    if (!fetchRes.ok) throw new Error(`HTTP ${fetchRes.status}`);
                    const blob = await fetchRes.blob();
                    const blobUrl = URL.createObjectURL(blob);
                    window.open(blobUrl, '_blank');
                  } catch (err) {
                    toast.error('Failed to load preview PDF');
                  }
                }}
              >
                <Button type="button" variant="outline" size="sm" data-testid="brd-preview-pdf-btn">
                  <FileText className="h-3.5 w-3.5 mr-1" /> Preview PDF
                </Button>
              </a>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button
            onClick={send}
            disabled={sending || loading || !preview || preview.invoice_count === 0 || phones.length === 0}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="brd-send-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <MessageSquare className="h-4 w-4 mr-1" />}
            Send Statement Reminder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkReminderDialog;
